import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { adminLogin, fetchContacts, fetchAdminStats, updateContactStatus } from "@/lib/apiClient";
import { toast, Toaster } from "sonner";
import { LogOut, RefreshCw, ChevronDown } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "closed"];

const StatusBadge = ({ status }) => {
  const map = {
    new: "bg-[#0055FF]/15 text-[#0055FF] border-[#0055FF]/30",
    contacted: "bg-white/10 text-white/80 border-white/20",
    qualified: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
    closed: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
  };
  return (
    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${map[status] || map.new}`} data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("pragma_admin_token") || "");
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem("pragma_admin_token")));
  const [loginInput, setLoginInput] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, qualified: 0, closed: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [c, s] = await Promise.all([fetchContacts(token), fetchAdminStats(token)]);
      setContacts(c);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired.");
        localStorage.removeItem("pragma_admin_token");
        setAuthed(false);
        setToken("");
      } else {
        toast.error("Failed to load contacts.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await adminLogin(loginInput.trim());
      localStorage.setItem("pragma_admin_token", res.token);
      setToken(res.token);
      setAuthed(true);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid admin token.");
    } finally {
      setLoginLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem("pragma_admin_token");
    setAuthed(false);
    setToken("");
    setContacts([]);
    setSelected(null);
  };

  const onStatus = async (id, status) => {
    try {
      await updateContactStatus(token, id, status);
      toast.success(`Updated to ${status}`);
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  if (!authed) {
    return (
      <>
        <Toaster theme="dark" position="top-right" richColors />
        <section className="min-h-[80vh] bg-[#0a0a0a] text-white flex items-center" data-testid="admin-login-section">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 w-full grid grid-cols-12 gap-10">
            <div className="col-span-12 md:col-span-6">
              <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-6">Admin</p>
              <h1 className="font-display text-6xl md:text-7xl tracking-tighter leading-[0.9]">
                Lead <br />operations<span className="text-[#0055FF]">.</span>
              </h1>
              <p className="mt-8 text-white/60 max-w-md">
                Authenticated dashboard for the Pragma growth team to triage incoming contracts.
              </p>
            </div>
            <form onSubmit={onLogin} className="col-span-12 md:col-span-5 md:col-start-8 border border-white/10 p-8" data-testid="admin-login-form">
              <Label className="text-xs uppercase tracking-widest text-white/50">Admin token</Label>
              <Input
                data-testid="admin-token-input"
                type="password"
                value={loginInput}
                onChange={(e) => setLoginInput(e.target.value)}
                placeholder="Enter token"
                className="rounded-none bg-transparent border-white/20 text-white h-12 mt-2 focus-visible:ring-[#0055FF] focus-visible:border-[#0055FF]"
              />
              <Button type="submit" disabled={loginLoading} className="mt-6 w-full bg-[#0055FF] hover:bg-[#0044cc] text-white rounded-none h-12" data-testid="admin-login-button">
                {loginLoading ? "Verifying…" : "Enter dashboard"}
              </Button>
              <p className="text-[11px] text-white/30 mt-4">
                Tokens are issued by Pragma operations. Default dev token: <code className="text-white/60">pragma-admin-2025</code>
              </p>
            </form>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <section className="bg-[#0a0a0a] text-white min-h-screen" data-testid="admin-dashboard">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-white/40 mb-2">Admin · Submissions</p>
              <h1 className="font-display text-5xl tracking-tighter leading-none">Inbound pipeline.</h1>
            </div>
            <div className="flex gap-2">
              <Button onClick={load} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white hover:text-black rounded-none h-11" data-testid="admin-refresh">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
              <Button onClick={onLogout} className="bg-white text-black hover:bg-zinc-200 rounded-none h-11" data-testid="admin-logout">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-white/10 mb-12">
            {[
              { l: "Total", v: stats.total, t: "stat-total" },
              { l: "New", v: stats.new, t: "stat-new" },
              { l: "Qualified", v: stats.qualified, t: "stat-qualified" },
              { l: "Closed", v: stats.closed, t: "stat-closed" },
            ].map((s) => (
              <div key={s.l} className="p-6 border-r border-b border-white/10" data-testid={s.t}>
                <p className="font-display text-4xl tracking-tighter">{s.v}</p>
                <p className="text-xs uppercase tracking-widest text-white/50 mt-2">{s.l}</p>
              </div>
            ))}
          </div>

          <div className="border border-white/10" data-testid="admin-table">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-white/10 text-xs uppercase tracking-widest text-white/40">
              <div className="col-span-3">Contact</div>
              <div className="col-span-2">Company</div>
              <div className="col-span-2">Service</div>
              <div className="col-span-2">Budget</div>
              <div className="col-span-2">Received</div>
              <div className="col-span-1 text-right">Status</div>
            </div>

            {contacts.length === 0 && (
              <div className="px-6 py-16 text-center text-white/50" data-testid="admin-empty">
                {loading ? "Loading…" : "No submissions yet."}
              </div>
            )}

            {contacts.map((c) => (
              <div
                key={c.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b border-white/10 hover:bg-white/[0.02] cursor-pointer"
                onClick={() => setSelected(c)}
                data-testid={`contact-row-${c.id}`}
              >
                <div className="col-span-3">
                  <p className="font-medium tracking-tight">{c.name}</p>
                  <p className="text-xs text-white/50">{c.email}</p>
                </div>
                <div className="col-span-2 text-sm text-white/70">{c.company}</div>
                <div className="col-span-2 text-sm text-white/70">{c.service_interest || "—"}</div>
                <div className="col-span-2 text-sm text-white/70">{c.budget || "—"}</div>
                <div className="col-span-2 text-xs text-white/50">{new Date(c.created_at).toLocaleString()}</div>
                <div className="col-span-1 flex md:justify-end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1" data-testid={`status-menu-${c.id}`}>
                        <StatusBadge status={c.status} />
                        <ChevronDown className="w-3 h-3 text-white/50" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="rounded-none bg-[#0a0a0a] border-white/20 text-white">
                      <DropdownMenuLabel className="text-xs uppercase tracking-widest text-white/40">Set status</DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-white/10" />
                      {STATUS_OPTIONS.map((s) => (
                        <DropdownMenuItem key={s} onClick={() => onStatus(c.id, s)} data-testid={`status-set-${c.id}-${s}`} className="capitalize">
                          {s}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selected && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center z-50 p-4" onClick={() => setSelected(null)} data-testid="contact-detail-modal">
            <div className="bg-[#0a0a0a] border border-white/15 max-w-2xl w-full p-8" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="font-display text-3xl tracking-tighter">{selected.name}</h2>
                  <p className="text-sm text-white/60 mt-1">{selected.email} · {selected.company}</p>
                </div>
                <StatusBadge status={selected.status} />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm border-t border-white/10 pt-6">
                <div><p className="text-xs uppercase tracking-widest text-white/40">Role</p><p>{selected.role || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-white/40">Phone</p><p>{selected.phone || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-white/40">Industry</p><p>{selected.industry || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-white/40">Service</p><p>{selected.service_interest || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-white/40">Budget</p><p>{selected.budget || "—"}</p></div>
                <div><p className="text-xs uppercase tracking-widest text-white/40">Timeline</p><p>{selected.timeline || "—"}</p></div>
              </div>
              <div className="mt-6 border-t border-white/10 pt-6">
                <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Project brief</p>
                <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
              <div className="mt-8 flex justify-end gap-2">
                <Button onClick={() => setSelected(null)} variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white hover:text-black rounded-none h-10" data-testid="detail-close">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
};

export default Admin;
